import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../../context/AuthContext';
import ConfirmModal from '../../../../components/ConfirmModal';

const SiteAccess = ({ site }) => {
  const { currentLang, fetchWithAuth, API_URL, getCsrfToken, user } = useAuth();
  
  const [accessList, setAccessList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newUserInput, setNewUserInput] = useState('');
  const [addingUser, setAddingUser] = useState(false);
  const [removingUser, setRemovingUser] = useState(new Set());
  
  // Состояние для модалки удаления доступа
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToRemove, setUserToRemove] = useState(null);

  // Проверяем, является ли текущий пользователь владельцем сайта
  const isOwner = user?.id === site?.ucr;

  useEffect(() => {
    if (isOwner) {
      fetchAccessList();
    }
  }, [site?.id, isOwner]);

  const fetchAccessList = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await fetchWithAuth(`${API_URL}${currentLang}/seo/sites/${site.id}/access`);

      const data = await response.json();

      if (response.ok && data.success) {
        setAccessList(data.data || []);
      } else {
        throw new Error(data.message || 'Ошибка загрузки списка доступа');
      }
    } catch (error) {
      if (error.message === 'Требуется авторизация') {
        setError('Сессия истекла. Пожалуйста, войдите снова.');
      } else {
        setError(error.message);
      }
      console.error('Ошибка загрузки списка доступа:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async () => {
    if (!newUserInput.trim()) {
      setError(currentLang === 'ru' ? 'Введите ID пользователя или email' : 'Enter user ID or email');
      return;
    }

    setAddingUser(true);
    setError('');

    try {
      const currentCsrfToken = await getCsrfToken();
      if (!currentCsrfToken) {
        throw new Error('Не удалось получить CSRF токен');
      }

      // Определяем, что введено: ID или email
      const isEmail = newUserInput.includes('@');
      const requestBody = isEmail 
        ? { email: newUserInput.trim() }
        : { user_id: parseInt(newUserInput.trim()) };

      const response = await fetchWithAuth(`${API_URL}${currentLang}/seo/sites/${site.id}/access`, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': currentCsrfToken,
        },
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setNewUserInput('');
        fetchAccessList(); // Обновляем список
        showNotification(
          currentLang === 'ru' ? 'Пользователь успешно добавлен!' : 'User added successfully!',
          'success'
        );
      } else {
        throw new Error(data.message || 'Ошибка добавления пользователя');
      }
    } catch (error) {
      if (error.message === 'Требуется авторизация') {
        setError('Сессия истекла. Пожалуйста, войдите снова.');
      } else {
        setError(error.message);
      }
      console.error('Ошибка добавления пользователя:', error);
    } finally {
      setAddingUser(false);
    }
  };

  // Показать модалку подтверждения удаления доступа
  const showRemoveConfirm = (userId) => {
    setUserToRemove(userId);
    setShowDeleteModal(true);
  };

  // Подтверждение удаления доступа пользователя
  const handleRemoveConfirm = async () => {
    if (!userToRemove) return;

    setRemovingUser(prev => new Set(prev).add(userToRemove));

    try {
      const currentCsrfToken = await getCsrfToken();
      if (!currentCsrfToken) {
        throw new Error('Не удалось получить CSRF токен');
      }

      const response = await fetchWithAuth(`${API_URL}${currentLang}/seo/sites/${site.id}/access/${userToRemove}`, {
        method: 'DELETE',
        headers: {
          'X-CSRF-Token': currentCsrfToken,
        },
      });

      const data = await response.json();

      if (response.ok && data.success) {
        fetchAccessList(); // Обновляем список
        showNotification(
          currentLang === 'ru' ? 'Доступ пользователя удален!' : 'User access removed!',
          'success'
        );
      } else {
        throw new Error(data.message || 'Ошибка удаления доступа');
      }
    } catch (error) {
      if (error.message === 'Требуется авторизация') {
        setError('Сессия истекла. Пожалуйста, войдите снова.');
      } else {
        setError(error.message);
      }
      console.error('Ошибка удаления доступа:', error);
    } finally {
      setRemovingUser(prev => {
        const newSet = new Set(prev);
        newSet.delete(userToRemove);
        return newSet;
      });
      setUserToRemove(null);
    }
  };

  // Закрыть модалку удаления
  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setUserToRemove(null);
  };

  const showNotification = (message, type = 'success') => {
    const colors = {
      success: 'bg-green-500',
      error: 'bg-red-500',
      info: 'bg-blue-500',
      warning: 'bg-yellow-500'
    };
    
    const notification = document.createElement('div');
    notification.className = `fixed top-4 right-4 ${colors[type]} text-white px-6 py-3 rounded-lg shadow-lg z-50 transform transition-all duration-300 translate-x-full`;
    notification.textContent = message;
    document.body.appendChild(notification);
    
    // Анимация появления
    setTimeout(() => {
      notification.classList.remove('translate-x-full');
    }, 100);
    
    // Автоматическое скрытие через 3 секунды
    setTimeout(() => {
      notification.classList.add('translate-x-full');
      setTimeout(() => {
        if (document.body.contains(notification)) {
          document.body.removeChild(notification);
        }
      }, 300);
    }, 3000);
  };

  // Если пользователь не владелец, показываем сообщение
  if (!isOwner) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="text-center py-8 text-gray-500">
          {currentLang === 'ru' ? 'Только владелец сайта может управлять доступом' : 'Only the site owner can manage access'}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold mb-4">
          {currentLang === 'ru' ? 'Управление доступом к сайту' : 'Site Access Management'}
        </h3>
        
        {/* Форма добавления пользователя */}
        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <h4 className="text-md font-medium mb-3">
            {currentLang === 'ru' ? 'Добавить пользователя' : 'Add User'}
          </h4>
          <div className="flex space-x-3">
            <input
              type="text"
              value={newUserInput}
              onChange={(e) => setNewUserInput(e.target.value)}
              placeholder={currentLang === 'ru' ? 'Введите ID пользователя или email' : 'Enter user ID or email'}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
            <button
              onClick={handleAddUser}
              disabled={addingUser || !newUserInput.trim()}
              className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {addingUser ? (
                <div className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>{currentLang === 'ru' ? 'Добавление...' : 'Adding...'}</span>
                </div>
              ) : (
                currentLang === 'ru' ? 'Добавить' : 'Add'
              )}
            </button>
          </div>
          <p className="text-sm text-gray-600 mt-2">
            {currentLang === 'ru' ? 'Введите ID пользователя (число) или email адрес' : 'Enter user ID (number) or email address'}
          </p>
        </div>

        {/* Ошибка */}
        {error && (
          <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
            {error}
          </div>
        )}
      </div>

      {/* Список пользователей с доступом */}
      <div>
        <h4 className="text-md font-medium mb-3">
          {currentLang === 'ru' ? 'Пользователи с доступом' : 'Users with Access'}
        </h4>
        
        {loading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500"></div>
          </div>
        ) : accessList.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            {currentLang === 'ru' ? 'Нет пользователей с доступом' : 'No users with access'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {currentLang === 'ru' ? 'ID' : 'ID'}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {currentLang === 'ru' ? 'Имя' : 'Name'}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {currentLang === 'ru' ? 'Email' : 'Email'}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {currentLang === 'ru' ? 'Действия' : 'Actions'}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {accessList.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {user.id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {user.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {user.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <button
                        onClick={() => showRemoveConfirm(user.id)}
                        disabled={removingUser.has(user.id)}
                        className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md disabled:opacity-50"
                        title={currentLang === 'ru' ? 'Удалить доступ' : 'Remove access'}
                      >
                        {removingUser.has(user.id) ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                        ) : (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        )}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Модалка подтверждения удаления доступа */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={closeDeleteModal}
        onConfirm={handleRemoveConfirm}
        title={currentLang === 'ru' ? 'Подтверждение удаления доступа' : 'Confirm Access Removal'}
        message={currentLang === 'ru' 
          ? 'Вы уверены, что хотите удалить доступ этого пользователя? Это действие нельзя отменить.' 
          : 'Are you sure you want to remove this user\'s access? This action cannot be undone.'
        }
        confirmText={currentLang === 'ru' ? 'Удалить' : 'Remove'}
        cancelText={currentLang === 'ru' ? 'Отмена' : 'Cancel'}
        confirmButtonClass="bg-red-600 hover:bg-red-700"
        currentLang={currentLang}
      />
    </div>
  );
};

export default SiteAccess; 